import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { Confirmation } from '@abp/ng.theme.shared';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { DialogModule } from 'primeng/dialog';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { RatingModule } from 'primeng/rating';
import { CxMultiSelectComponent } from 'src/app/shared/components/cx-multi-select/cx-multi-select.component';
import { CxUserAvatarComponent } from 'src/app/shared/components/cx-user-avatar/cx-user-avatar.component';
import { SkillDto } from 'src/core/models/skill/skill.dto';
import { SkillUserDto } from 'src/core/models/skill/skill-user.dto';
import { AgentLookupDto } from 'src/core/models/agent/agent-lookup.dto';
import { AgentService } from 'src/core/services/agent/agent.service';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { AgentRow } from '../../models/agent-row.model';
import { AgentOption } from '../../models/agent-option.model';


type SkillWithAgentCount = SkillDto;

@Component({
  selector: 'app-skill-manage-agents-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, LocalizationPipe, DialogModule, CxMultiSelectComponent, CxUserAvatarComponent, RatingModule, FormActionsComponent],
  templateUrl: './skill-manage-agents-dialog.component.html',
})
export class SkillManageAgentsDialogComponent implements OnChanges {
  private readonly crudService = inject(CrudService);
  private readonly agentService = inject(AgentService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toasterService = inject(ToasterService);
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() visible = false;
  @Input() skill: SkillWithAgentCount | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  allAgents: AgentLookupDto[] = [];
  alreadyAssigned: AgentRow[] = [];
  newAgents: AgentRow[] = [];
  selectedAgentIds: string[] = [];
  isAssignedExpanded = false;
  applyToNewLevel: number | null = null;
  loading = false;
  saving = false;

  get availableAgentOptions(): AgentOption[] {
    const usedIds = new Set([
      ...this.alreadyAssigned.map(a => a.userId.toLowerCase()),
      ...this.newAgents.map(a => a.userId.toLowerCase()),
    ]);
    return this.allAgents
      .filter(a => !usedIds.has(a.id.toLowerCase()))
      .map(a => ({ id: a.id, displayName: this.getAgentDisplayName(a) }));
  }

  get canSave(): boolean {
    const all = [...this.alreadyAssigned, ...this.newAgents];
    return all.every(r => (r.level ?? 0) > 0);
  }

  get agentsSelectedLabel(): string {
    return `{0} ${this.localization.instant('Settings::WorkEnvironment:Skills:AgentsSelected')}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.skill) {
      this.resetState();
      this.loadAgents();
    }
  }

  onAdd(): void {
    if (!this.selectedAgentIds.length) return;

    const toAdd: AgentRow[] = this.selectedAgentIds.map(id => {
      const agent = this.allAgents.find(a => a.id.toLowerCase() === id.toLowerCase());
      return {
        userId: id,
        name: this.getAgentName(agent),
        displayName: agent ? this.getAgentDisplayName(agent) : id,
        level: 3,
      };
    });

    this.newAgents = [...this.newAgents, ...toAdd];
    this.selectedAgentIds = [];
    this.cdr.markForCheck();
  }

  onRemoveAssigned(index: number): void {
    if (this.isLastRemainingAgent()) {
      this.confirmLastAgentRemoval(() => {
        this.alreadyAssigned = this.alreadyAssigned.filter((_, i) => i !== index);
        this.cdr.markForCheck();
      });
      return;
    }

    this.alreadyAssigned = this.alreadyAssigned.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  onRemoveNew(index: number): void {
    if (this.isLastRemainingAgent()) {
      this.confirmLastAgentRemoval(() => {
        this.newAgents = this.newAgents.filter((_, i) => i !== index);
        this.cdr.markForCheck();
      });
      return;
    }

    this.newAgents = this.newAgents.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  onApplyToNew(): void {
    if (!this.applyToNewLevel) return;
    const level = this.applyToNewLevel;
    this.newAgents = this.newAgents.map(a => ({ ...a, level }));
    this.cdr.markForCheck();
  }

  onUnassignAll(): void {
    if (!this.skill) return;

    const message = this.localization.instant(
      'Settings::WorkEnvironment:Skills:UnassignAllConfirmMessage',
      this.skill.name,
      String(this.alreadyAssigned.length),
    );

    this.confirmationService
      .warn(message, 'Settings::WorkEnvironment:Skills:UnassignAllConfirmTitle', {
        hideCancelBtn: false,
        hideYesBtn: false,
        dismissible: true,
        yesText: 'Settings::WorkEnvironment:Skills:UnassignAll',
        cancelText: 'AbpUi::Cancel',
      })
      .pipe(
        switchMap(status => {
          if (status !== Confirmation.Status.confirm) return of(null);
          return this.crudService.update(SkillDto, {
            id: this.skill!.id,
            name: this.skill!.name,
            isActive: this.skill!.isActive,
            assignedUsers: [],
          });
        }),
      )
      .subscribe({
        next: result => {
          if (result === null) return;
          this.toasterService.success('Settings::WorkEnvironment:Skills:UnassignAllSuccess');
          this.alreadyAssigned = [];
          this.isAssignedExpanded = false;
          this.saved.emit();
          this.visibleChange.emit(false);
          this.cdr.markForCheck();
        },
      });
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onSave(): void {
    if (!this.skill || !this.canSave || this.saving) return;

    this.saving = true;
    const skillId = this.skill.id;

    const assignedUsers: SkillUserDto[] = [
      ...this.alreadyAssigned.map(r => ({ skillId, userId: r.userId, level: r.level ?? 0 })),
      ...this.newAgents.map(r => ({ skillId, userId: r.userId, level: r.level ?? 0 })),
    ];

    this.crudService
      .update(SkillDto, {
        id: skillId,
        name: this.skill.name,
        isActive: this.skill.isActive,
        assignedUsers,
      })
      .pipe(finalize(() => {
        this.saving = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.toasterService.success('Settings::WorkEnvironment:Skills:ManageAgentsSaveSuccess');
          this.saved.emit();
          this.visibleChange.emit(false);
        },
      });
  }

  trackByUserId(_: number, row: AgentRow): string {
    return row.userId;
  }

  private isLastRemainingAgent(): boolean {
    return this.alreadyAssigned.length + this.newAgents.length === 1;
  }

  private confirmLastAgentRemoval(onConfirm: () => void): void {
    this.confirmationService
      .warn(
        'Settings::WorkEnvironment:Skills:LastAgentMessage',
        'Settings::WorkEnvironment:Skills:LastAgentTitle',
      )
      .subscribe(status => {
        if (status === Confirmation.Status.confirm) {
          onConfirm();
        }
      });
  }

  private getAgentDisplayName(agent: AgentLookupDto): string {
    return agent.name ? `${agent.name} (${agent.userName})` : agent.userName;
  }

  /** Plain name of an agent, for the avatar's initials fallback; empty when the agent is unknown. */
  private getAgentName(agent: AgentLookupDto | undefined): string {
    return agent?.name?.trim() || agent?.userName?.trim() || '';
  }

  private resetState(): void {
    this.alreadyAssigned = [];
    this.newAgents = [];
    this.selectedAgentIds = [];
    this.isAssignedExpanded = false;
    this.applyToNewLevel = 3;
    this.saving = false;
  }

  private loadAgents(): void {
    const skillId = this.skill?.id;
    if (!skillId) {
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    forkJoin({
      // Available agents lead the picker's list; the backend applies that ordering in its query.
      agents: this.agentService.getAgentLookup(true),
      skill: this.crudService.getById<SkillDto>(SkillDto, skillId),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ agents, skill }) => {
          this.allAgents = agents;
          this.alreadyAssigned = (skill?.assignedUsers ?? []).map(skillUser => {
            const agent = agents.find(a => a.id.toLowerCase() === skillUser.userId.toLowerCase());
            return {
              userId: skillUser.userId,
              name: this.getAgentName(agent),
              displayName: agent ? this.getAgentDisplayName(agent) : skillUser.userId,
              level: skillUser.level,
            };
          });
        },
        error: () => {
          this.allAgents = [];
          this.alreadyAssigned = [];
        },
      });
  }
}
